import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";

interface CountUpProps {
  end: number;
  start?: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  onComplete?: () => void;
}

export function CountUp({
  end,
  start = 0,
  duration = 1,
  decimals = 0,
  prefix = "",
  suffix = "",
  className,
  onComplete,
}: CountUpProps) {
  const [value, setValue] = useState(start);
  const [isComplete, setIsComplete] = useState(false);
  const frameRef = useRef<number>();
  const startTimeRef = useRef<number>();

  useEffect(() => {
    startTimeRef.current = undefined;
    setIsComplete(false);

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / (duration * 1000), 1);

      // Ease out cubic for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = start + (end - start) * easeProgress;

      setValue(currentValue);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setValue(end);
        setIsComplete(true);
        onComplete?.();
      }
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [end, start, duration, onComplete]);

  const formattedValue = value.toFixed(decimals);

  // Format with commas for Indian numbering
  const formatIndian = (num: string) => {
    const parts = num.split(".");
    let intPart = parts[0];
    const decPart = parts[1];

    // Indian numbering: 12,34,567
    const lastThree = intPart.slice(-3);
    const otherNumbers = intPart.slice(0, -3);
    if (otherNumbers !== "") {
      intPart = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
    } else {
      intPart = lastThree;
    }

    return decPart ? `${intPart}.${decPart}` : intPart;
  };

  return (
    <span
      className={cn(
        "tabular-nums transition-smooth",
        isComplete && "text-foreground",
        className
      )}
    >
      {prefix}
      {formatIndian(formattedValue)}
      {suffix}
    </span>
  );
}

// Delta variant that shows +/- with color
interface DeltaCountUpProps extends Omit<CountUpProps, "prefix"> {
  showSign?: boolean;
}

export function DeltaCountUp({
  end,
  showSign = true,
  className,
  ...props
}: DeltaCountUpProps) {
  const isPositive = end > 0;
  const isNegative = end < 0;
  const isNeutral = end === 0;

  const prefix = showSign ? (isPositive ? "+" : isNegative ? "" : "") : "";

  return (
    <CountUp
      end={end}
      prefix={prefix}
      className={cn(
        isPositive && "text-success",
        isNegative && "text-danger",
        isNeutral && "text-muted-foreground",
        className
      )}
      {...props}
    />
  );
}
